/**
 * 80. POS Session — open/close session; cash counts; sales; refunds;
 * end-of-day reconciliation.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate, formatMoney } from "@/lib/money";

interface PosSessionRow {
  id: string;
  sessionNumber: string;
  terminal: string;
  openedAt: string | null;
  closedAt: string | null;
  openingCash: string;
  closingCash: string;
  salesTotal: string;
  refundTotal: string;
  currency: string;
  status: "Open" | "Closed" | "Reconciled";
}

export function PosSessionsPage() {
  const { data, isLoading } = useApiList<PosSessionRow>(
    ["pos", "sessions"],
    "/pos/sessions",
  );

  return (
    <div>
      <PageHeader
        title="POS Sessions"
        subtitle="Open/close sessions, cash counts, sales, refunds, and end-of-day reconciliation."
        actions={<Button variant="primary">Open session</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Sessions (${data.total})`}>
          <DataTable<PosSessionRow>
            rows={data.items}
            columns={[
              {
                key: "sessionNumber",
                label: "Session",
                render: (r) => (
                  <span className="atlas-mono">{r.sessionNumber}</span>
                ),
              },
              { key: "terminal", label: "Terminal" },
              {
                key: "openedAt",
                label: "Opened",
                render: (r) => (r.openedAt ? formatDate(r.openedAt) : "—"),
              },
              {
                key: "closedAt",
                label: "Closed",
                render: (r) => (r.closedAt ? formatDate(r.closedAt) : "—"),
              },
              {
                key: "openingCash",
                label: "Opening cash",
                numeric: true,
                render: (r) => formatMoney(r.openingCash, r.currency),
              },
              {
                key: "salesTotal",
                label: "Sales",
                numeric: true,
                render: (r) => formatMoney(r.salesTotal, r.currency),
              },
              {
                key: "refundTotal",
                label: "Refunds",
                numeric: true,
                render: (r) => formatMoney(r.refundTotal, r.currency),
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Reconciled" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Open" ? (
                    <Badge tone="success">{r.status}</Badge>
                  ) : (
                    <Badge tone="warning">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
